import { Injectable } from '@angular/core';
import { ImageEnhancements, DEFAULT_ENHANCEMENTS } from './enhancement.service';

export interface ProjectData {
  version: string;
  timestamp: number;
  originalImage: string | null;
  correctedImage: string | null;
  perspectiveCorrected: boolean;
  backgroundRemoved: boolean;
  enhancements: ImageEnhancements;
  composition: CompositionState;
  background: BackgroundState;
  shadowLayer: ShadowLayerState;
}

export interface CompositionState {
  rotation: number;
  flipH: boolean;
  flipV: boolean;
  imagePositionX: number;
  imagePositionY: number;
  imageScale: number;
}

export interface BackgroundState {
  type: 'solid' | 'gradient' | 'image';
  color: string;
  gradientColor1: string;
  gradientColor2: string;
  gradientAngle: number;
  imageDataUrl: string | null;
  imageScale: number;
  imagePositionX: number;
  imagePositionY: number;
  imageOpacity: number;
  imageBlur: number;
}

export interface ShadowLayerState {
  enabled: boolean;
  presetId: string;
  scale: number;
  offsetX: number;
  offsetY: number;
  opacity: number;
  rotation: number;
  blur?: number;
  shape?: 'silhouette' | 'ellipse' | 'rectangle';
}

const PROJECT_VERSION = '1.0';

@Injectable({
  providedIn: 'root'
})
export class ProjectService {

  /**
   * Create project data object from current state
   */
  createProjectData(
    originalImage: HTMLImageElement | null,
    correctedImage: HTMLImageElement | null,
    perspectiveCorrected: boolean,
    backgroundRemoved: boolean,
    enhancements: ImageEnhancements,
    composition: CompositionState,
    background: BackgroundState,
    shadowLayer: ShadowLayerState
  ): ProjectData {
    return {
      version: PROJECT_VERSION,
      timestamp: Date.now(),
      originalImage: originalImage ? this.imageToDataUrl(originalImage) : null,
      correctedImage: correctedImage ? this.imageToDataUrl(correctedImage) : null,
      perspectiveCorrected,
      backgroundRemoved,
      enhancements,
      composition,
      background,
      shadowLayer
    };
  }

  /**
   * Serialize project to JSON string
   */
  serializeProject(project: ProjectData): string {
    return JSON.stringify(project);
  }

  /**
   * Deserialize project from JSON string
   */
  deserializeProject(json: string): ProjectData {
    return JSON.parse(json);
  }

  /**
   * Download project as JSON file
   */
  downloadProject(project: ProjectData, filename: string = 'artflow-project.json'): void {
    const json = this.serializeProject(project);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Save project to localStorage
   */
  saveToLocalStorage(project: ProjectData, key: string = 'artflow-project'): void {
    const json = this.serializeProject(project);
    localStorage.setItem(key, json);
  }

  /**
   * Load project from localStorage
   */
  loadFromLocalStorage(key: string = 'artflow-project'): ProjectData | null {
    const json = localStorage.getItem(key);
    if (!json) return null;
    try {
      return this.deserializeProject(json);
    } catch {
      return null;
    }
  }

  /**
   * Load project from file
   */
  loadProjectFromFile(file: File): Promise<ProjectData> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const json = e.target?.result as string;
          const project = this.deserializeProject(json);
          resolve(project);
        } catch (error) {
          reject(new Error('Invalid project file'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  /**
   * Load image from data URL
   */
  loadImageFromDataUrl(dataUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = dataUrl;
    });
  }

  /**
   * Convert image to data URL
   */
  private imageToDataUrl(image: HTMLImageElement): string {
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(image, 0, 0);
      return canvas.toDataURL('image/png');
    }
    return '';
  }

  /**
   * Get default composition state
   */
  getDefaultComposition(): CompositionState {
    return {
      rotation: 0,
      flipH: false,
      flipV: false,
      imagePositionX: 50,
      imagePositionY: 50,
      imageScale: 100
    };
  }

  /**
   * Get default background state
   */
  getDefaultBackground(): BackgroundState {
    return {
      type: 'solid',
      color: '#ffffff',
      gradientColor1: '#ffffff',
      gradientColor2: '#e0e0e0',
      gradientAngle: 180,
      imageDataUrl: null,
      imageScale: 100,
      imagePositionX: 50,
      imagePositionY: 50,
      imageOpacity: 100,
      imageBlur: 0
    };
  }

  /**
   * Get default shadow layer state
   */
  getDefaultShadowLayer(): ShadowLayerState {
    return {
      enabled: false,
      presetId: 'none',
      scale: 100,
      offsetX: 0,
      offsetY: 20,
      opacity: 50,
      rotation: 0
    };
  }
}
